import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AlertTriangle, Check, X } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { useAuthStore } from "../state/authStore";
import { postJSON } from "../lib/api";

const PLAN_INFO = {
    pro: { name: "Pro · Operate", price: "$49", cadence: "per user / month" },
    msp: { name: "MSP · Orchestrate", price: "$199", cadence: "per seat / month" },
};

export const MockCheckout = () => {
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const { refresh } = useAuthStore();
    const sessionId = params.get("session_id");
    const plan = params.get("plan") || "pro";
    const info = PLAN_INFO[plan] || PLAN_INFO.pro;

    const [status, setStatus] = useState("idle");
    const [err, setErr] = useState("");

    const complete = async (success) => {
        if (status === "loading") return;
        setStatus("loading");
        setErr("");
        try {
            await postJSON("/billing/mock/complete", { session_id: sessionId, success });
            await refresh();
            if (success) {
                navigate("/dashboard?welcome=1", { replace: true });
            } else {
                navigate("/pricing?canceled=1", { replace: true });
            }
        } catch (e) {
            setStatus("idle");
            setErr(e.message || "Could not complete checkout.");
        }
    };

    return (
        <div className="pt-24 md:pt-28 pb-12 mx-auto max-w-2xl px-5" data-testid="mock-checkout-page">
            <div
                className="mb-6 rounded-md border border-accent-amber/40 bg-accent-amber/[0.06] px-3 py-2.5 text-[12px] font-mono uppercase tracking-[0.22em] text-accent-amber flex items-center gap-2"
                data-testid="mock-payment-banner"
            >
                <AlertTriangle className="w-3.5 h-3.5" />
                MOCK PAYMENT — NO REAL CARD CHARGED
            </div>

            <Reveal>
                <div className="rounded-2xl border border-white/[0.06] bg-surface/70 backdrop-blur-md p-7">
                    <p className="text-[10.5px] font-mono uppercase tracking-[0.32em] text-accent-cyan">
                        Checkout
                    </p>
                    <h1 className="mt-3 font-display font-semibold text-3xl tracking-tight" data-testid="mock-checkout-title">
                        {info.name}
                    </h1>
                    <p className="mt-2 text-text-muted text-[13.5px]">Subscription · {info.cadence}</p>

                    <div className="mt-6 grid grid-cols-2 gap-3 text-[13px]">
                        <div className="rounded-lg border border-white/[0.06] bg-canvas/60 p-4">
                            <p className="text-[10.5px] font-mono uppercase tracking-[0.22em] text-text-dim">
                                Subtotal
                            </p>
                            <p className="mt-1 text-text-primary font-semibold">{info.price}</p>
                        </div>
                        <div className="rounded-lg border border-white/[0.06] bg-canvas/60 p-4">
                            <p className="text-[10.5px] font-mono uppercase tracking-[0.22em] text-text-dim">
                                Tax
                            </p>
                            <p className="mt-1 text-text-primary font-semibold">$0.00</p>
                        </div>
                    </div>
                    <div className="mt-3 rounded-lg border border-accent-cyan/30 bg-accent-cyan/[0.04] p-4 flex items-center justify-between">
                        <p className="text-[11px] font-mono uppercase tracking-[0.2em] text-accent-cyan">
                            Total today
                        </p>
                        <p className="font-display font-semibold text-2xl text-text-primary tracking-tight">
                            {info.price}
                        </p>
                    </div>

                    {err && (
                        <div
                            className="mt-4 text-[12px] text-accent-red bg-accent-red/5 border border-accent-red/20 rounded-md px-3 py-2"
                            data-testid="mock-checkout-error"
                        >
                            {err}
                        </div>
                    )}

                    <div className="mt-6 flex flex-col sm:flex-row gap-3">
                        <button
                            type="button"
                            onClick={() => complete(true)}
                            disabled={status === "loading"}
                            className="flex-1 inline-flex items-center justify-center gap-2 py-3 rounded-md bg-accent-mint text-canvas text-[13.5px] font-semibold hover:shadow-glow-mint disabled:opacity-40 transition-shadow duration-200"
                            data-testid="mock-pay-btn"
                        >
                            <Check className="w-4 h-4" />
                            Pay {info.price}
                        </button>
                        <button
                            type="button"
                            onClick={() => complete(false)}
                            disabled={status === "loading"}
                            className="flex-1 inline-flex items-center justify-center gap-2 py-3 rounded-md border border-white/[0.08] text-text-muted text-[13.5px] font-medium hover:text-text-primary hover:border-white/[0.18] disabled:opacity-40 transition-colors duration-200"
                            data-testid="mock-cancel-btn"
                        >
                            <X className="w-4 h-4" />
                            Cancel
                        </button>
                    </div>
                    <p className="mt-4 text-[11px] text-text-dim font-mono">
                        Session: {sessionId}
                    </p>
                </div>
            </Reveal>
        </div>
    );
};

export default MockCheckout;
